import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { Button } from 'antd';
import styled from 'styled-components';

import { bulkUpdateCompanyPreferences, getCompanyPreferences, updateCompanyPreferences } from 'apis/SettingsAPI';
import { useRootContext } from 'components/layout/RootContext';
import Notification from 'components/Notification/Notification';

import { DEFAULT_TABLE_PAGE_SIZE, FETCH_COMPANY_PREFERENCES_PAGE_SIZE } from './constants';
import { IncentiveScrappingFilterValue } from './enums';
import IncentiveScrappingBulkEditModal from './IncentiveScrappingBulkEditModal';
import IncentiveScrappingCompanyList from './IncentiveScrappingCompanyList';
import IncentiveScrappingHeader from './IncentiveScrappingHeader';
import IncentiveScrappingToolbar from './IncentiveScrappingToolbar';
import { CompanyPreferenceItem, CompanyPreferencesResponse, IncentiveScrappingStatusFilter } from './types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
`;

const Content = styled.div`
  padding: 0 30px 30px;
  width: 100%;
  box-sizing: border-box;
  align-self: stretch;
`;

const EditMultipleButton = styled(Button)`
  background-color: var(--button-blue);
  border-color: var(--button-blue);
  padding: 10px 20px;
  height: 40px;
  font-family: var(--font-regular);
  border-radius: 4px;

  &:hover {
    background-color: #2051f2 !important;
    border-color: #2051f2 !important;
  }
`;

const queryKey = ['incentive-scrapping', 'company-preferences'];

const fetchAllCompanyPreferences = async () => {
  let page = 1;
  const allItems: CompanyPreferenceItem[] = [];

  let hasNextPage = true;

  while (hasNextPage) {
    const response: CompanyPreferencesResponse | CompanyPreferenceItem[] = await getCompanyPreferences({
      page,
      page_size: FETCH_COMPANY_PREFERENCES_PAGE_SIZE,
    });

    if (Array.isArray(response)) {
      return response;
    }

    const batch = response?.results ?? [];
    allItems.push(...batch);

    hasNextPage = Boolean(response?.next);
    if (hasNextPage) {
      page += 1;
    }
  }

  return allItems;
};

const IncentiveScrapping = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useRootContext();

  const [isMultipleSelection, setMultipleSelection] = useState(false);
  const [status, setStatus] = useState<IncentiveScrappingStatusFilter>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [updatingCompanyIds, setUpdatingCompanyIds] = useState<number[]>([]);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);

  const { data, isLoading, isFetching, isError } = useQuery<CompanyPreferenceItem[]>({
    queryKey,
    queryFn: fetchAllCompanyPreferences,
    refetchOnWindowFocus: false,
  });

  const { mutate: mutatePreference } = useMutation({
    mutationFn: ({ companyId, enabled }: { companyId: number; enabled: boolean }) =>
      updateCompanyPreferences(companyId, { incentive_scrapping: enabled }),
    onMutate: async ({ companyId, enabled }) => {
      setUpdatingCompanyIds((prev) => (prev.includes(companyId) ? prev : [...prev, companyId]));

      const previousItems = queryClient.getQueryData<CompanyPreferenceItem[]>(queryKey) ?? [];

      queryClient.setQueryData<CompanyPreferenceItem[]>(
        queryKey,
        previousItems.map((item) =>
          item.company === companyId
            ? {
                ...item,
                incentive_scrapping: enabled,
              }
            : item
        )
      );

      return { previousItems };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData<CompanyPreferenceItem[]>(queryKey, context.previousItems);
      }
      showMessage('error', 'Failed to update company preference');
    },
    onSuccess: () => {
      showMessage('success', 'Company preference updated');
    },
    onSettled: (_data, _error, variables) => {
      setUpdatingCompanyIds((prev) => prev.filter((id) => id !== variables.companyId));
    },
  });

  const { mutate: mutateBulkPreferences, isLoading: isBulkUpdating } = useMutation({
    mutationFn: async ({ companyIds, enabled }: { companyIds: number[]; enabled: boolean }) => {
      const payload = companyIds.map((companyId) => ({ company: companyId, incentive_scrapping: enabled }));
      await bulkUpdateCompanyPreferences(payload);
    },
    onMutate: async ({ companyIds, enabled }) => {
      setUpdatingCompanyIds((prev) => Array.from(new Set([...prev, ...companyIds])));

      const previousItems = queryClient.getQueryData<CompanyPreferenceItem[]>(queryKey) ?? [];

      queryClient.setQueryData<CompanyPreferenceItem[]>(
        queryKey,
        previousItems.map((item) =>
          companyIds.includes(item.company)
            ? {
                ...item,
                incentive_scrapping: enabled,
              }
            : item
        )
      );

      return { previousItems, companyIds };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData<CompanyPreferenceItem[]>(queryKey, context.previousItems);
      }
      showMessage('error', 'Failed to update selected companies');
    },
    onSuccess: () => {
      showMessage('success', 'Selected companies updated');
      setBulkModalOpen(false);
      setSelectedCompanyIds([]);
      setMultipleSelection(false);
    },
    onSettled: (_data, _error, variables) => {
      setUpdatingCompanyIds((prev) => prev.filter((id) => !variables.companyIds.includes(id)));
    },
  });

  const filteredItems = useMemo(() => {
    const allItems = data ?? [];
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    return allItems.filter((item) => {
      if (status !== null) {
        const isOn = status === IncentiveScrappingFilterValue.On;
        if (item.incentive_scrapping !== isOn) {
          return false;
        }
      }

      if (normalizedSearchTerm && !item.company_name.toLowerCase().includes(normalizedSearchTerm)) {
        return false;
      }

      return true;
    });
  }, [data, status, searchTerm]);

  const selectedCompaniesForEdit = useMemo(
    () => (data ?? []).filter((item) => selectedCompanyIds.includes(item.company)).map((item) => item.company_name),
    [data, selectedCompanyIds]
  );

  const paginatedItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredItems.slice(start, end);
  }, [filteredItems, page, pageSize]);

  const allVisibleSelected =
    paginatedItems.length > 0 && paginatedItems.every((item) => selectedCompanyIds.includes(item.company));

  const handleToggleAllVisible = () => {
    if (allVisibleSelected) {
      const visibleIds = paginatedItems.map((item) => item.company);
      setSelectedCompanyIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }

    const merged = new Set(selectedCompanyIds);
    paginatedItems.forEach((item) => merged.add(item.company));
    setSelectedCompanyIds(Array.from(merged));
  };

  const handleSelectAllFiltered = () => {
    setSelectedCompanyIds(filteredItems.map((item) => item.company));
  };

  const handleToggleOne = (companyId: number) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  };

  const handleTogglePreference = (companyId: number, enabled: boolean) => {
    if (updatingCompanyIds.includes(companyId)) {
      return;
    }

    mutatePreference({ companyId, enabled });
  };

  const handleApplyBulkUpdate = (enabled: boolean) => {
    if (selectedCompanyIds.length === 0) {
      return;
    }

    mutateBulkPreferences({ companyIds: selectedCompanyIds, enabled });
  };

  return (
    <Container>
      <Notification
        allItemsCount={filteredItems.length}
        totalCount={paginatedItems.length}
        selectedCount={selectedCompanyIds.length}
        singularLabel={'company'}
        multipleLabel={'companies'}
        onSelectAllItems={handleSelectAllFiltered}
        onClose={() => {
          setSelectedCompanyIds([]);
          setMultipleSelection(false);
        }}
        actions={
          <EditMultipleButton type="primary" onClick={() => setBulkModalOpen(true)}>
            Edit Multiple
          </EditMultipleButton>
        }
      />

      <IncentiveScrappingBulkEditModal
        isOpen={bulkModalOpen}
        loading={isBulkUpdating}
        selectedCompanyNames={selectedCompaniesForEdit}
        onClose={() => setBulkModalOpen(false)}
        onSave={handleApplyBulkUpdate}
      />

      <IncentiveScrappingHeader />
      <Content>
        <IncentiveScrappingToolbar
          isMultipleSelection={isMultipleSelection}
          onToggleMultipleSelection={() => {
            const nextValue = !isMultipleSelection;
            setMultipleSelection(nextValue);
            if (!nextValue) {
              setSelectedCompanyIds([]);
            }
          }}
          status={status}
          setStatus={setStatus}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setPage={setPage}
        />

        <IncentiveScrappingCompanyList
          isMultipleSelection={isMultipleSelection}
          allVisibleSelected={allVisibleSelected}
          onToggleAllVisible={handleToggleAllVisible}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          paginatedItems={paginatedItems}
          filteredItemsCount={filteredItems.length}
          selectedCompanyIds={selectedCompanyIds}
          updatingCompanyIds={updatingCompanyIds}
          onToggleOne={handleToggleOne}
          onTogglePreference={handleTogglePreference}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      </Content>
    </Container>
  );
};

export default IncentiveScrapping;
